import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, Check, CheckCheck, Loader2, BellOff, Info, UserPlus, FileText, CloudOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { UnreadBadge } from '@/components/layout/sidebar-primitives';
import { api } from '@/services/api';
import { routeFor } from '@/hooks/usePushDeepLink';
import { useNotificationStream } from '@/hooks/useNotificationStream';
import { timeAgo } from '@/lib/utils';
import type { Notification } from '@/types/api';

/**
 * Badge reconciliation cadence (#555 review, finding 6): even while the SSE
 * stream is live, keep a slow 10-minute belt-and-braces poll so a silently
 * wedged stream (or a missed push) can never strand the badge forever.
 * Without a stream, the 120s poll (D-W2-22 rate rationale — do not shorten
 * it) is the badge source, exactly as before the stream existed.
 */
export function badgeRefetchInterval(streamActive: boolean): number {
  return streamActive ? 600_000 : 120_000;
}

/**
 * The text of one notification (#2109 R3-B12). A row a parent-facing creator
 * wrote carries `message_key` + `params`: render that key in the reader’s
 * language. Old rows, staff rows and a key this build does not know fall back
 * to the stored English `message` — nothing is backfilled. Only a
 * `notifications:` key is honoured, so a row can never render another
 * namespace’s string.
 */
type LooseT = (key: string, params?: Record<string, unknown>) => string;
function notificationText(n: Notification, t: LooseT, exists: (key: string) => boolean): string {
  const key = n.message_key;
  if (key && key.startsWith('notifications:') && exists(key)) return t(key, n.params ?? {});
  return n.message;
}

function getNotificationIcon(type: string) {
  switch (type) {
    // #2354: task_claimed, task_cancelled and circle_invitation had cases
    // here and no backend producer, so they were removed.
    case 'task_completed':
      return <Check className="h-4 w-4 text-status-ok-fg" />;
    case 'pending_signup':
      return <UserPlus className="h-4 w-4 text-stevens-indigo" />;
    // #2341: the Grants nav row's glyph (parent-nav.ts), so the row looks
    // like the page a tap lands on. A decision is a KIND here, not a state —
    // approved and not approved wear the same mark, and the words say which
    // (the state-or-kind rule: a "not approved" row must not wear an error hue).
    // #2972: a payment staff recorded (`grant_payment`, in-app only) lands
    // on the same Grants page, so it wears the same mark.
    case 'grant_decision':
    case 'grant_payment':
      return <FileText className="h-4 w-4 text-cat-teal-fg" />;
    default:
      return <Info className="h-4 w-4 text-stevens-ink-3" />;
  }
}


export function NotificationPanel() {
  // The header chrome's words (#2109); notification messages are backend data.
  const { t, i18n } = useTranslation(['shell', 'notifications', 'common']);
  const textOf = (n: Notification) =>
    notificationText(n, t as unknown as LooseT, (key) => i18n.exists(key));
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Live badge: user-scoped SSE push (D-W3-5, #518). The server pushes a
  // fresh unread-count on notification create / mark-as-read / read-all —
  // write it straight into the count query's cache and mark the list stale
  // (the list query is enabled only while the panel is open; when closed
  // this just flags it for the on-open refetch).
  const { streamActive } = useNotificationStream({
    onUnreadCount: useCallback(
      (count: number) => {
        queryClient.setQueryData(['notification-count'], count);
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
      },
      [queryClient],
    ),
    // Visible-again refetch (#555 review, finding 7): the hook tears the
    // stream down after 60s hidden and react-query pauses background polls,
    // so on return nudge the badge once — refetchOnWindowFocus is globally
    // disabled (main.tsx) and would not do it for us.
    onVisible: useCallback(() => {
      queryClient.invalidateQueries({ queryKey: ['notification-count'] });
    }, [queryClient]),
  });

  const { data: countData } = useQuery({
    queryKey: ['notification-count'],
    queryFn: async () => {
      const response = await api.notifications.getUnreadCount();
      return response.data?.count ?? 0;
    },
    // Push replaces the fast poll while the SSE stream is live (D-W3-5,
    // #518), but NOT all polling: a slow 10-min reconciliation poll stays on
    // (#555 review, finding 6 — belt-and-braces against a wedged stream).
    // The 120s poll (D-W2-22 rate rationale — do not shorten it) is the
    // DEPLOY-SKEW FALLBACK: old backend without /notifications/stream,
    // dropped connection, or exhausted reconnects all leave
    // streamActive=false, and the badge falls back to polling.
    refetchInterval: badgeRefetchInterval(streamActive),
  });

  const unreadCount = countData ?? 0;

  const {
    data: notificationsData,
    isLoading,
    isError: listFailed,
    refetch: refetchList,
  } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const response = await api.notifications.getAll();
      return response.data ?? [];
    },
    enabled: open,
  });

  const notifications = notificationsData ?? [];
  // #2382: a list that never loaded is not an empty inbox. React Query keeps
  // `data` when a later refetch fails, so the error shows only when there is
  // nothing to show instead — a failed refresh keeps the rows already loaded.
  const showLoadError = listFailed && notificationsData === undefined;

  const markAsReadMutation = useMutation({
    mutationFn: (id: string) => api.notifications.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notification-count'] });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: () => api.notifications.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notification-count'] });
    },
  });

  // Opening the panel refreshes the badge count immediately so the slower
  // 2-min poll (D-W2-22) never leaves a stale badge next to a fresh list.
  // The list query itself refetches on open via `enabled: open` + default
  // staleTime 0, so only the count needs an explicit nudge.
  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      queryClient.invalidateQueries({ queryKey: ['notification-count'] });
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read_at) {
      markAsReadMutation.mutate(notification._id);
    }

    setOpen(false);

    // pending_signup is an admin-only in-app type not in the shared push map.
    // Lands on the live TRFer roster rather than /admin/signups (which
    // redirects here anyway since the 2026-09-20 admin-section subtraction)
    // so the tap doesn't pay for a hop it doesn't need.
    if (notification.notification_type === 'pending_signup') {
      navigate('/trfer/users?status=pending');
      return;
    }

    // Delegate to the canonical push→route map so an in-app tap lands on the
    // same screen as the equivalent push tap — single source of truth, mirrors
    // PushBanner. routeFor reads a flat {type, *_id} shape, so bridge it from
    // the notification's top-level task_id + metadata. Unknown types fall through
    // to routeFor's safe /dashboard default (never a phantom route — B-065).
    const data: Record<string, string> = { type: notification.notification_type };
    const taskId = notification.task_id ?? notification.metadata?.task_id;
    if (taskId) data.task_id = taskId;
    if (notification.metadata?.bill_id) data.bill_id = notification.metadata.bill_id;
    if (notification.metadata?.grant_id) data.grant_id = notification.metadata.grant_id;
    if (notification.metadata?.conversation_id) data.conversation_id = notification.metadata.conversation_id;
    if (notification.metadata?.request_id) data.request_id = notification.metadata.request_id;
    if (notification.metadata?.deep_link) data.link = notification.metadata.deep_link;
    navigate(routeFor(data));
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="relative h-11 w-11 shrink-0"
              aria-label={
                unreadCount > 0
                  ? t('notifications.titleWithCount', { count: unreadCount })
                  : t('notifications.title')
              }
            >
              {/* 16px glyph in the 44px target (§2.8 rule 6), and the count in
                  the sidebar's indigo `UnreadBadge` — the one chip every unread
                  number on the platform wears (§2.7). Not a red status: an
                  unread count is chrome, not a danger. The chip is hidden from
                  the tree and the count reaches a screen reader through the
                  button's name, "Notifications, 9 unread" (#2422) — the same
                  shape as the staff UnreadsBell, never a run-on
                  "Notifications 9". */}
              <Bell className="h-4 w-4" />
              <span aria-hidden="true" className="absolute -top-0.5 -right-0.5">
                <UnreadBadge count={unreadCount} />
              </span>
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">{t('notifications.title')}</TooltipContent>
      </Tooltip>
      <PopoverContent align="end" className="w-80 p-0">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3">
          <h4 className="text-body font-semibold">{t('notifications.title')}</h4>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto p-0 text-caption text-stevens-ink-3 hover:text-foreground"
              onClick={() => markAllAsReadMutation.mutate()}
              disabled={markAllAsReadMutation.isPending}
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              {t('notifications.markAllRead')}
            </Button>
          )}
        </div>
        <Separator />

        {/* Notification List */}
        <div className="max-h-80 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-stevens-ink-3" />
            </div>
          ) : showLoadError ? (
            <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
              <CloudOff className="h-8 w-8 text-stevens-ink-3 mb-2" />
              <p className="text-body text-stevens-ink-3">{t('notifications.loadError')}</p>
              <Button variant="ghost" size="sm" className="mt-2" onClick={() => refetchList()}>
                {t('common:actions.retry')}
              </Button>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 px-4">
              <BellOff className="h-8 w-8 text-stevens-ink-3 mb-2" />
              <p className="text-body text-stevens-ink-3">{t('notifications.empty')}</p>
            </div>
          ) : (
            notifications.slice(0, 20).map((notification) => (
              // The whole row is the control: a tap marks it read and opens
              // the page routeFor names.
              <div
                key={notification._id}
                role="button"
                tabIndex={0}
                aria-label={textOf(notification)}
                className={`w-full text-left px-4 py-3 hover:bg-accent transition-colors border-b last:border-b-0 cursor-pointer ${
                  !notification.read_at ? 'bg-accent/50' : ''
                }`}
                onClick={() => handleNotificationClick(notification)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleNotificationClick(notification);
                  }
                }}
              >
                <div className="flex gap-3">
                  <div className="mt-0.5 shrink-0">
                    {getNotificationIcon(notification.notification_type)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-body ${!notification.read_at ? 'font-medium' : 'text-stevens-ink-3'}`}>
                      {textOf(notification)}
                    </p>
                    <p className="text-caption text-stevens-ink-3 mt-1">
                      {timeAgo(notification.created_at)}
                    </p>
                  </div>
                  {!notification.read_at && (
                    <div className="mt-1.5 shrink-0">
                      <div className="h-2 w-2 rounded-full bg-primary" />
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
